import React, { useState, useEffect } from 'react';
import { useSettings } from '../../hooks/useSettings';
import { useGlobalError } from '../../context/GlobalErrorContext';
import { LoadingSpinner } from '../Dashboard/UXStates';

const BillingTab = () => {
  const { getBilling, getSubscriptions, createSubscription, cancelSubscription } = useSettings();
  const { addError } = useGlobalError();
  const [billing, setBilling] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [billingData, subsData] = await Promise.all([
        getBilling(),
        getSubscriptions()
      ]);
      setBilling(billingData);
      setSubscriptions(subsData);
    } catch (err) {
      addError(err, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpgrade = async (agentIdOverride) => {
    setActionLoading(true);
    try {
      const agentId = agentIdOverride || billing?.limits?.agentId;
      if (!agentId) {
        addError('No agent ID available. Please contact support.', { type: 'error' });
        setActionLoading(false);
        return;
      }
      const result = await createSubscription(agentId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        addError('Subscription created successfully!', { type: 'success', timeout: 3000 });
        fetchData();
      }
    } catch (err) {
      addError(err, { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? You will lose access at the end of the billing period.')) {
      return;
    }
    setActionLoading(true);
    try {
      await cancelSubscription(subscriptionId);
      addError('Subscription canceled. You will retain access until the end of the billing period.', { type: 'success', timeout: 5000 });
      fetchData();
    } catch (err) {
      addError(err, { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="settings-section"><LoadingSpinner message="Loading billing information..." /></div>;
  }

  const { usage, limits } = billing || { usage: null, limits: null };
  const activeSubscriptions = subscriptions.filter(s => s.status === 'active' || s.status === 'trialing');
  const canceledSubscriptions = subscriptions.filter(s => s.status === 'canceled');
  const hasActiveSubscription = activeSubscriptions.length > 0;

  return (
    <div className="settings-section">
      <h2>Billing & Subscription</h2>
      <p className="description">Manage your subscription and view usage</p>

      <div className="billing-card">
        <h3>Current Plan</h3>
        {hasActiveSubscription ? (
          <>
            <div className="plan-name">
              {activeSubscriptions[0]?.agents?.name || 'Premium Plan'}
            </div>
            <div className="plan-price">
              {activeSubscriptions[0]?.agents?.price_monthly 
                ? `$${activeSubscriptions[0].agents.price_monthly / 100}/month`
                : 'Active'}
            </div>
            <div className="plan-status">
              <span className="status-badge status-active">Active</span>
              {activeSubscriptions[0]?.current_period_end && (
                <span className="renewal-date">
                  Renews {new Date(activeSubscriptions[0].current_period_end).toLocaleDateString()}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="plan-name">Free Tier</div>
            <div className="plan-price">Limited to free usage</div>
          </>
        )}
      </div>

      {hasActiveSubscription && (
        <div className="settings-section">
          <h3>Active Subscriptions</h3>
          {activeSubscriptions.map((sub) => (
            <div key={sub.id} className="subscription-item">
              <div className="subscription-info">
                <div className="subscription-agent">{sub.agents?.name || 'Agent'}</div>
                <div className="subscription-meta">
                  <span>Status: <strong>{sub.status}</strong></span>
                  <span>Period: {new Date(sub.current_period_start).toLocaleDateString()} - {new Date(sub.current_period_end).toLocaleDateString()}</span>
                </div>
              </div>
              <button 
                className="btn-danger-outline"
                onClick={() => handleCancelSubscription(sub.id)}
                disabled={actionLoading}
              >
                Cancel Subscription
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="settings-section">
        <h3>Plan Limits</h3>
        <div className="billing-stat">
          <span className="billing-stat-label">Monthly Runs Limit</span>
          <span className="billing-stat-value">
            {limits?.maxRunsMonthly === -1 ? 'Unlimited' : limits?.maxRunsMonthly || 'N/A'}
          </span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat-label">Max Deployments</span>
          <span className="billing-stat-value">{limits?.maxDeployments || 'N/A'}</span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat-label">Max API Keys</span>
          <span className="billing-stat-value">{limits?.maxApiKeys || 'N/A'}</span>
        </div>
      </div>

      <div className="settings-section">
        <h3>Usage This Month</h3>
        <div className="billing-stat">
          <span className="billing-stat-label">Total Runs</span>
          <span className="billing-stat-value">{usage?.totalRuns || 0}</span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat-label">Credits Used</span>
          <span className="billing-stat-value">{usage?.totalCredits || 0}</span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat-label">Platform Cost</span>
          <span className="billing-stat-value">
            {usage?.platformCostCents ? `$${(usage.platformCostCents / 100).toFixed(2)}` : '$0.00'}
          </span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat-label">Billing Period</span>
          <span className="billing-stat-value">
            {usage?.periodStart && usage?.periodEnd
              ? `${new Date(usage.periodStart).toLocaleDateString()} - ${new Date(usage.periodEnd).toLocaleDateString()}`
              : 'N/A'}
          </span>
        </div>
      </div>

      {!hasActiveSubscription && (
        <div className="settings-section upgrade-section">
          <h3>Upgrade Your Plan</h3>
          <p className="description">Get more runs, deployments, and premium features</p>
          <button 
            className="btn-primary" 
            onClick={handleUpgrade}
            disabled={actionLoading}
          >
            {actionLoading ? 'Processing...' : 'Upgrade to Premium'}
          </button>
        </div>
      )}

      {canceledSubscriptions.length > 0 && (
        <div className="settings-section">
          <h3>Canceled Subscriptions</h3>
          {canceledSubscriptions.map((sub) => (
            <div key={sub.id} className="subscription-item canceled">
              <div className="subscription-info">
                <div className="subscription-agent">{sub.agents?.name || 'Agent'}</div>
                <div className="subscription-meta">
                  <span>Ended: {new Date(sub.current_period_end).toLocaleDateString()}</span>
                </div>
              </div>
              <button 
                className="btn-secondary"
                onClick={() => handleUpgrade(sub.agents?.id || sub.agent_id)}
                disabled={actionLoading}
              >
                Resubscribe
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingTab;
